import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { ApplicationState } from '../../../store';
import * as SharedMaterialsManagementStateController from '../../../store/StateControllers/SharedMaterials/SharedMaterialsManagementStateController';
import UpdateCompanyRequest from '../../../store/Models/Api/UpdateCompanyRequest';
import { SharedMaterialsFullCompany, SharedMaterialsEditableCompany } from "../../../store/Models/SharedMaterials/DataModel";
import { ToxnotComponentProperties } from "../../core/BaseApplicationComponents/ToxnotComponent";
import { BaseUiState, MainApplicationViewState, ToxnotMainApplicationViewComponent } from "../../core/BaseApplicationComponents/ToxnotMainApplicationViewComponent";
import { SidebarDetail } from "../../core/SidebarDetail";
import CompanyPageEditor, { PublishingOptions } from "./CompanyPageEditor";
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../../core/ToxnotReactModal';
import { Prompt } from 'react-router';

type CompanyPagesManagerProps = ToxnotComponentProperties &
    SharedMaterialsManagementStateController.ManagementState;

enum CompanyPagesManagerUiState {
    Loading,
    Failed,
    Loaded
}

interface CompanyPagesManagerState extends MainApplicationViewState<CompanyPagesManagerUiState> {
    companyPages: SharedMaterialsEditableCompany[];
    activeSidebarNavIndex: number;
    companyPageBeingEdited: boolean;
}

export class CompanyPagesManager extends ToxnotMainApplicationViewComponent<CompanyPagesManagerProps, CompanyPagesManagerUiState, CompanyPagesManagerState, UpdateCompanyRequest> {
    constructor(props: CompanyPagesManagerProps) {
        super(props);

        this.state = {
            uiState: BaseUiState.Loading,
            companyPages: [],
            activeSidebarNavIndex: -1,
            companyPageBeingEdited: false
        };
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    protected saveDataToService(data: UpdateCompanyRequest): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    // ===============================================================================

    protected getPostInitialLoadUiState(): CompanyPagesManagerUiState {
        return CompanyPagesManagerUiState.Loaded;
    }

    // ===============================================================================

    protected async fetchViewDataAndRefreshState(): Promise<void> {
        const apiResult = await this._serviceClient.sharedMaterialsCompaniesGet();

        if (!this.apiDataFetchWasSuccessful(apiResult)) {
            this.enterFailedState();

            return;
        }

        const companyPagesFromService = apiResult.data!.orderBy(p => p.name);

        const companyPagesForState: SharedMaterialsEditableCompany[] = [];

        for (let p of companyPagesFromService) {
            companyPagesForState.push({
                ...p,
                localModelId: uuidv4()
            });
        }

        if (companyPagesForState.length === 0) {
            companyPagesForState.push(this.createEmptyCompanyPage());
        }

        this.setState({
            uiState: CompanyPagesManagerUiState.Loaded,
            companyPages: companyPagesForState,
            activeSidebarNavIndex: 0
        });
    }

    // ===============================================================================

    protected getLocalizedViewTitle(): string | null {
        return this.localizeString("Create Brands For Your Passports");
    }

    // ===============================================================================

    private getCompanyName(name: string) {
        return name.trim().length === 0 ? this.localizeString("Unnamed Brand") : name;
    }

    // ===============================================================================

    private renderTopActionsBar() {
        return (
            <div className="tn-section-row">
                <div className="tn-section-col-12">
                    <div className="company-pages-manager-top-action-bar-container">
                        <div className="company-pages-manager-action-buttons-container">
                            <button id="add-brand-button" className="btn btn-default" onClick={e => this.addCompanyPage()}><span className="glyphicon glyphicon-plus"></span> {this.localizeString("Brand")}</button> <button id="remove-brand-button" className="btn btn-danger" onClick={e => this.removeCompanyPage_click()}><span className="glyphicon glyphicon-remove"></span> {this.localizeString("Brand")}</button>
                        </div>
                        <div className="company-pages-manager-status-container">
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    protected renderViewContent(): JSX.Element | null {
        return (
            <>
                {this.renderTopActionsBar()}
                <SidebarDetail
                    key={this.state.activeSidebarNavIndex}
                    localizedSidebarNavTitles={this.state.companyPages.map(p => this.getCompanyName(p.name))}
                    initialActiveIndex={this.state.activeSidebarNavIndex}
                    onSidebarNavItemClicked={this.sideBarNavItem_click}
                    shouldUpdateNavIndex={this.shouldUpdateNavIndex}
                    onlyChildToRender={
                        this.state.companyPages.length === 0 ? 
                            <div className="text-center">{this.localizeString("Add a Brand To Get Started")}</div> :
                            <CompanyPageEditor
                                key={this.state.companyPages[this.state.activeSidebarNavIndex].localModelId}
                                initialCompany={this.state.companyPages[this.state.activeSidebarNavIndex]}
                                onCompanyPagePublish={this.company_onPublish}
                                onCompanyBeingEdited={(isBeingEdited: boolean) => this.setState({ companyPageBeingEdited: isBeingEdited })}
                            />}
                />                    
                <ToxnotReactModal modalId={"unable-to-remove-company-page-modal"} localizedTitle={"This Brand Has Passports"}>
                    <ToxnotReactModalBody>
                        <p>
                            {this.localizeString("You are not able to remove brands that have published passports linked to them.")}
                        </p>
                        <p>
                            {this.localizeString("To remove this brand you must first remove all your passports on the Passport Library that were published under this brand.")}
                        </p>
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter localizer={this.props.appServices} />
                </ToxnotReactModal>
                <ToxnotReactModal modalId={"remove-company-page-confirmation-modal"} localizedTitle={"Are you sure you want to remove this brand?"}>
                    <ToxnotReactModalBody>
                        <p>
                            {this.localizeString("You are about to remove the brand")} <b>{(this.state.companyPages.length > this.state.activeSidebarNavIndex ? this.state.companyPages[this.state.activeSidebarNavIndex].name : "")}</b>
                        </p>
                        <p>
                            {this.localizeString("Removing this brand will remove it from the Passport Library, where manufactures can find and contact you.")}
                        </p>
                        <p>
                            {this.localizeString("Are you sure you want to remove?")}
                        </p>
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter localizer={this.props.appServices}>
                        <button className="btn btn-danger" onClick={e => this.removeCompanyPageModalConfirm_click()}>{this.localizeString("Yes, Remove")}</button>
                    </ToxnotReactModalFooter>
                </ToxnotReactModal>
                <Prompt
                    when={this.state.companyPageBeingEdited}
                    message={this.localizeString("You have unsaved changes. Are you sure you want to leave?")} />
            </>
        );
    }

    // ===============================================================================

    private createEmptyCompanyPage() {
        const companyPage: SharedMaterialsEditableCompany = {
            ownerId: this.getUserContextForSignedInUser().teamOwnerId,
            name: "",
            logoFileId: null,
            contactEmail: "",
            customUrlName: null,
            companyInfo: {
                description: "",
                websiteUrl: ""
            },
            reports: [],
            companyId: null,
            localModelId: uuidv4()
        }

        return companyPage;
    }

    // ===============================================================================

    private shouldUpdateNavIndex = () => {
        if (this.state.companyPageBeingEdited) {
            alert(this.localizeString("You must save your changes first."));
            return false;
        }

        return true;
    }

    // ===============================================================================

    private updateCompanyPageInState(companyPage: SharedMaterialsEditableCompany) {
        const newCompanyPages = this.state.companyPages.map(p => {
            if (p.localModelId !== companyPage.localModelId) {
                return p;
            }

            return {
                ...companyPage
            }
        });

        this.setState({
            companyPages: newCompanyPages
        });
    }

    // ===============================================================================

    private async removeCompanyPage() {
        const newCompanyPages = [...this.state.companyPages];

        const companyPageToRemove = this.state.companyPages[this.state.activeSidebarNavIndex];

        if (companyPageToRemove.companyId !== null) {
            const apiResult = await this._serviceClient.sharedMaterialsCompaniesDelete([companyPageToRemove.companyId]);

            if (!this.apiFetchWasSuccessful(apiResult)) {
                this.enterFailedState();

                return;
            }
        }

        newCompanyPages.splice(this.state.activeSidebarNavIndex, 1);

        this.setState({
            companyPages: newCompanyPages,
            activeSidebarNavIndex: Math.max(0, this.state.activeSidebarNavIndex - 1),
            companyPageBeingEdited: false
        });
    }

    // ===============================================================================

    protected data_needsToBeSaved = () => {
        return this.state.companyPageBeingEdited;
    }

    // ===============================================================================

    //
    // UI Events
    //

    private company_onPublish = (updatedCompany: SharedMaterialsEditableCompany, successful: boolean) => {
        if (!successful) {
            this.enterFailedState();
        }

        this.updateCompanyPageInState(updatedCompany);

        this.setState({
            companyPageBeingEdited: false // only one company page can be edited at once
        });
    }

    // ===============================================================================

    private addCompanyPage = () => {
        if (this.state.companyPageBeingEdited) {
            alert(this.localizeString("You must save your changes first."))
            return;
        }

        const newCompanyPage = this.createEmptyCompanyPage();
        const newCompanyPages = [...this.state.companyPages];

        newCompanyPages.push(newCompanyPage);

        const index = newCompanyPages.length - 1;

        this.setState({
            companyPages: newCompanyPages,
            activeSidebarNavIndex: index
        });
    }

    // ===============================================================================

    private removeCompanyPage_click = () => {
        const companyPageToRemove = this.state.companyPages[this.state.activeSidebarNavIndex];

        if (Object.values(this.props.ownedSharedMaterialsMap).some(s => s.companyId === companyPageToRemove.companyId)) {
            $('#unable-to-remove-company-page-modal').modal('show');
            return;
        }

        $('#remove-company-page-confirmation-modal').modal('show');
    }

    // ===============================================================================

    private sideBarNavItem_click = (navIndex: number) => {
        this.setState({
            activeSidebarNavIndex: navIndex,
        });
    }

    // ===============================================================================

    private removeCompanyPageModalConfirm_click = () => {
        this.removeCompanyPage();
        $('#remove-company-page-confirmation-modal').modal('hide');
    }
}

export default connect(
    (state: ApplicationState) => ({ ...state.sharedMaterialsManagementState, appServices: state.appServices })
)(CompanyPagesManager as any);